import { NavLink, Outlet } from "react-router-dom";
import { BarChart3, Bot, CalendarDays, FileSpreadsheet, Home, LogOut, Settings, ShieldCheck, Sparkles, Target, User, Wallet } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import TopBar from "./TopBar";
import FinPipMark from "./FinPipMark";
import FeedbackButton from "./FeedbackButton";
import InstallBanner from "./InstallBanner";

// `labelKey` is an i18n key resolved through t() at render so the nav
// re-localizes when the language changes.
const NAV = [
  { to: "/", labelKey: "nav.dashboard", Icon: Home },
  { to: "/trades", labelKey: "nav.trades", Icon: BarChart3 },
  { to: "/wallet", labelKey: "nav.wallet", Icon: Wallet },
  { to: "/calendar", labelKey: "nav.calendar", Icon: CalendarDays },
  { to: "/journey", labelKey: "nav.journey", Icon: Target },
  { to: "/reports", labelKey: "nav.reports", Icon: Sparkles },
  { to: "/assistant", labelKey: "nav.assistant", Icon: Bot },
  { to: "/import", labelKey: "nav.import", Icon: FileSpreadsheet },
  { to: "/settings", labelKey: "nav.settings", Icon: Settings },
  { to: "/profile", labelKey: "nav.profile", Icon: User },
];

export default function Shell() {
  const { user, signOut } = useAuth();
  const { t } = useI18n();
  const isAdmin = Boolean((user as { is_admin?: boolean } | null)?.is_admin);

  return (
    <div className="min-h-screen">
      {/* Fixed sidebar — pinned to the inline-START edge (left in LTR, right in
          RTL); its divider uses a logical inline-end border so it flips too. */}
      <aside
        className="hidden lg:flex fixed start-0 top-0 h-screen w-64 flex-col p-4 backdrop-blur-xl z-40"
        style={{
          backgroundColor: "rgb(var(--bg-950) / 0.75)",
          borderInlineEnd: "1px solid rgb(var(--border-soft))",
        }}
      >
        <div
          className="px-2 pb-4"
          style={{ borderBottom: "1px solid rgb(var(--border-soft))" }}
        >
          <div className="flex items-center gap-2">
            <FinPipMark className="w-8 h-8 text-[#0c34f4] dark:text-white" />
            <div>
              <p className="text-parchment text-lg font-bold tracking-tight">{t("app.name")}</p>
              <p className="text-parchment-muted text-[10px] uppercase tracking-widest">
                {user?.name || user?.email}
              </p>
            </div>
          </div>
        </div>

        <nav className="mt-4 flex-1 space-y-1">
          {NAV.map(({ to, labelKey, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition nav-item" +
                (isActive ? " nav-item--active" : "")
              }
            >
              <Icon className="w-4 h-4 shrink-0" />
              {t(labelKey)}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition nav-item" +
                (isActive ? " nav-item--active" : "")
              }
            >
              <ShieldCheck className="w-4 h-4 shrink-0" />
              {t("nav.admin")}
            </NavLink>
          )}
        </nav>

        <div className="mt-2">
          <button
            type="button"
            onClick={() => void signOut()}
            className="btn-ghost w-full"
          >
            <LogOut className="w-4 h-4" />
            {t("nav.signOut")}
          </button>
        </div>
      </aside>

      {/* Global toolbar (language + timezone + theme) — fixed to the top
          inline-end corner on every page */}
      <TopBar />

      {/* Inline-start padding clears the sidebar; top padding clears the toolbar. */}
      <main className="px-4 sm:px-6 lg:ps-72 lg:pe-8 pt-16 sm:pt-20 pb-12 space-y-5 max-w-[1720px] mx-auto">
        <InstallBanner />
        <Outlet />
      </main>

      {/* Floating feedback launcher on every authenticated page */}
      <FeedbackButton />
    </div>
  );
}
